/**
 * Upload, capture, and video duration limits by plan.
 *
 * Successful completions are tracked in `users/{uid}/billing/metrics.captureTotals.totalCaptures`
 * (incremented on first transition to processing.status === "completed"). Failed pipelines increment
 * `captureTotals.totalFailedCaptures` on first transition to "failed".
 *
 * Free tier: lifetime completion cap + concurrent in-flight captures (see hooks/useProjects).
 * Professional / Studio: rolling 30-day window completion counts vs monthly allowance.
 * Enterprise: no monthly capture cap in-app (fair use).
 */

/**
 * Per-file byte ceiling for source video uploads (abuse / infra guard).
 * Plan differentiation is by video duration (getMaxVideoDurationSecondsForPlan), not these caps.
 */
export const SOURCE_VIDEO_UPLOAD_MAX_BYTES = 5 * 1024 * 1024 * 1024; // 5 GiB — typical high‑bitrate ~5 min clips
export const SOURCE_VIDEO_UPLOAD_MAX_BYTES_ENTERPRISE = 10 * 1024 * 1024 * 1024; // 10 GiB

/** @deprecated use SOURCE_VIDEO_UPLOAD_MAX_BYTES */
export const FREE_PLAN_UPLOAD_BYTES = SOURCE_VIDEO_UPLOAD_MAX_BYTES;
/** @deprecated use SOURCE_VIDEO_UPLOAD_MAX_BYTES */
export const PAID_PLAN_UPLOAD_BYTES = SOURCE_VIDEO_UPLOAD_MAX_BYTES;

/** @deprecated use PROFESSIONAL_MONTHLY_CAPTURE_LIMIT */
export const PRO_PLAN_CAPTURE_LIMIT = 15;

/**
 * Lifetime successful completions on free tier (matches pricing bullets).
 * Trial handoffs: ensure accounts that should self-serve are upgraded or show the upgrade modal
 * (see `UserCaptures` limit gate) before users hit this cap unexpectedly.
 */
export const FREE_PLAN_CAPTURE_LIMIT = 3;

export const PROFESSIONAL_MONTHLY_CAPTURE_LIMIT = 15;
export const STUDIO_MONTHLY_CAPTURE_LIMIT = 30;

export const ROLLING_CAPTURE_PERIOD_MS = 30 * 24 * 60 * 60 * 1000;

export const FREE_PLAN_MAX_VIDEO_DURATION_SEC = 60;
export const PROFESSIONAL_MAX_VIDEO_DURATION_SEC = 300;
export const STUDIO_MAX_VIDEO_DURATION_SEC = 600;
/** Client validation ceiling for enterprise-tier uploads (policy: at least 10 min). */
export const ENTERPRISE_MAX_VIDEO_DURATION_SEC = 4 * 60 * 60;

/** Total capture-tree storage caps (matches billing/metrics.storage.capBytes policy). */
export const FREE_STORAGE_CAP_BYTES = 25 * 1024 * 1024 * 1024;
export const PROFESSIONAL_STORAGE_CAP_BYTES = 50 * 1024 * 1024 * 1024;
export const STUDIO_STORAGE_CAP_BYTES = 100 * 1024 * 1024 * 1024;
export const ENTERPRISE_STORAGE_CAP_BYTES = 100 * 1024 * 1024 * 1024;

/** Normalize Firestore `subscription.plan` to canonical keys used in the app. */
export function normalizeSubscriptionPlan(plan) {
  const p = String(plan ?? 'free')
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '_');
  if (p === 'pro') return 'professional';
  if (p === 'unlimited') return 'enterprise';
  /** @deprecated legacy `subscription.plan` value — treat as Professional for limits */
  if (p === 'pay_per_capture') return 'professional';
  return p;
}

/** Password-protected / multi-capture project share links (Professional, Studio, Enterprise). */
export function canUseProjectShare(plan) {
  const p = normalizeSubscriptionPlan(plan);
  return p === 'professional' || p === 'studio' || p === 'enterprise';
}

/** Architecture scene timelines (curated snapshots, viewer strip, share-as-timeline). Professional+ */
export function canUseSceneTimelines(plan) {
  const p = normalizeSubscriptionPlan(plan);
  return p === 'professional' || p === 'studio' || p === 'enterprise';
}

/** GPU semantic index (SAM) runs for all tiers on the capture pipeline; any viewer can search once indexed. */
export function canBuildSemanticIndex(plan) {
  void plan;
  return true;
}

/** Max source-video file size for the given plan (technical ceiling; tier limits are duration-based). */
export function getMaxUploadBytes(plan) {
  const p = normalizeSubscriptionPlan(plan);
  if (p === 'enterprise') return SOURCE_VIDEO_UPLOAD_MAX_BYTES_ENTERPRISE;
  return SOURCE_VIDEO_UPLOAD_MAX_BYTES;
}

/** Human-readable limit for UI (e.g. "3 GB", "16 GB"). */
export function formatSingleFileUploadLimitLabel(bytes) {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n <= 0) return null;
  if (n >= 1024 * 1024 * 1024) {
    const gb = n / (1024 * 1024 * 1024);
    const rounded = gb >= 10 ? Math.round(gb) : Math.round(gb * 10) / 10;
    return `${rounded} GB`;
  }
  const mb = Math.max(1, Math.round(n / (1024 * 1024)));
  return `${mb} MB`;
}

/** Modal / toast copy when a file exceeds getMaxUploadBytes (technical cap); tier limits are duration-based. */
export function buildFileTooLargeUserMessage(maxBytes) {
  const label = formatSingleFileUploadLimitLabel(maxBytes);
  if (label) {
    return `Maximum size per file is ${label}. Plan limits are based on video length—compress or shorten your clip if needed.`;
  }
  return 'This file exceeds the maximum upload size. Plan limits are based on video length—try a smaller or shorter file.';
}

/** Short plan name for in-sentence copy ("The Free Plan allows…"). */
export function getSubscriptionPlanDisplayNameForCopy(plan) {
  const p = normalizeSubscriptionPlan(plan);
  const map = {
    free: 'Free',
    professional: 'Professional',
    studio: 'Studio',
    enterprise: 'Enterprise',
  };
  return map[p] ?? 'Free';
}

/** m:ss for modal copy (plans cap at hours today). */
function formatVideoDurationClock(totalSeconds) {
  if (totalSeconds == null || !Number.isFinite(totalSeconds)) return null;
  const s = Math.max(0, Math.round(totalSeconds));
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}:${String(sec).padStart(2, '0')}`;
}

/** Modal body when a source video exceeds getMaxVideoDurationSecondsForPlan(plan). */
export function buildVideoTooLongUserMessage(maxSec, actualSec, plan) {
  const planName = getSubscriptionPlanDisplayNameForCopy(plan);
  const actualClock = formatVideoDurationClock(actualSec);
  let allowance = null;
  if (maxSec != null && Number.isFinite(maxSec) && maxSec > 0) {
    if (maxSec < 60) {
      allowance = `The ${planName} Plan allows up to ${Math.round(maxSec)} seconds of video.`;
    } else if (maxSec < 3600) {
      const m = Math.round(maxSec / 60);
      allowance = `The ${planName} Plan allows up to ${m} minute${m === 1 ? '' : 's'} of video.`;
    } else {
      const h = Math.round(maxSec / 3600);
      allowance = `The ${planName} Plan allows up to ${h} hour${h === 1 ? '' : 's'} of video.`;
    }
  }
  if (!allowance) {
    return 'This video is longer than your plan allows. Please trim your clip or upgrade.';
  }
  const tail = actualClock
    ? ` This video is ${actualClock}. Please trim your clip or upgrade.`
    : ' Please trim your clip or upgrade.';
  return `${allowance}${tail}`;
}

/** Top bar / hints: "1 MINUTE PER UPLOAD", "2 MINUTES PER UPLOAD", etc. */
export function formatMaxVideoDurationPerUploadHint(plan) {
  const sec = getMaxVideoDurationSecondsForPlan(plan);
  if (!Number.isFinite(sec) || sec <= 0) return 'VIDEO DURATION LIMITED PER PLAN';
  if (sec < 60) return `${Math.round(sec)} SECONDS PER UPLOAD`;
  if (sec < 3600) {
    const m = Math.round(sec / 60);
    return `${m} MINUTE${m === 1 ? '' : 'S'} PER UPLOAD`;
  }
  const h = Math.round(sec / 3600);
  return `${h} HOUR${h === 1 ? '' : 'S'} PER UPLOAD`;
}

/** True if the user doc has admin flag (unlimited captures, bypass limits). */
export function isAdminUser(userData) {
  if (!userData || typeof userData !== 'object') return false;
  const a = userData.admin;
  return a === true || a === 'Yes' || a === 'yes';
}

/** Plan tier for UI and client-side gates — admins always resolve to enterprise. */
export function effectivePlanForUser(user) {
  if (isAdminUser(user)) return 'enterprise';
  return normalizeSubscriptionPlan(user?.plan || 'free');
}

export function hasLifetimeCaptureLimit(plan) {
  return normalizeSubscriptionPlan(plan) === 'free';
}

export function getMonthlyCaptureLimit(plan) {
  const p = normalizeSubscriptionPlan(plan);
  if (p === 'professional') return PROFESSIONAL_MONTHLY_CAPTURE_LIMIT;
  if (p === 'studio') return STUDIO_MONTHLY_CAPTURE_LIMIT;
  return null;
}

export function isUnlimitedMonthlyCaptures(plan) {
  return normalizeSubscriptionPlan(plan) === 'enterprise';
}

export function getMaxVideoDurationSecondsForPlan(plan) {
  const p = normalizeSubscriptionPlan(plan);
  if (p === 'free' || p === 'shopify_free') return FREE_PLAN_MAX_VIDEO_DURATION_SEC;
  if (p === 'professional' || p === 'shopify_starter') return PROFESSIONAL_MAX_VIDEO_DURATION_SEC;
  if (p === 'studio' || p === 'shopify_growth' || p === 'shopify_unlimited') return STUDIO_MAX_VIDEO_DURATION_SEC;
  if (p === 'enterprise') return ENTERPRISE_MAX_VIDEO_DURATION_SEC;
  return FREE_PLAN_MAX_VIDEO_DURATION_SEC;
}

/**
 * Total bytes allowed under users/{uid}/studio/.../captures (display / client-side hints).
 * @param {string} plan
 * @returns {number} bytes
 */
export function getStorageCapBytesForPlan(plan) {
  const p = normalizeSubscriptionPlan(plan);
  if (p === 'free') return FREE_STORAGE_CAP_BYTES;
  if (p === 'professional') return PROFESSIONAL_STORAGE_CAP_BYTES;
  if (p === 'studio') return STUDIO_STORAGE_CAP_BYTES;
  if (p === 'enterprise') return ENTERPRISE_STORAGE_CAP_BYTES;
  return FREE_STORAGE_CAP_BYTES;
}

/**
 * Effective capture storage cap (bytes). Honors `users/{uid}/billing/plan.storageCapOverrideBytes` when set (ops).
 * @param {Record<string, unknown> | null | undefined} billingPlanData - data() from billing/plan or partial
 * @param {unknown} planRaw - subscription.plan from user or billing plan doc
 * @returns {number}
 */
export function getStorageCapBytesForUserDoc(billingPlanData, planRaw) {
  const raw = billingPlanData?.storageCapOverrideBytes;
  const override = typeof raw === 'string' ? parseInt(raw, 10) : Number(raw);
  if (Number.isFinite(override) && override > 0) {
    return Math.floor(override);
  }
  return getStorageCapBytesForPlan(planRaw);
}
